import { DateTime, Duration } from "luxon";
import { locale } from "@/language/translation";

/**
 * converts a milliseconds in an hour string with two digits behind decimal point
 * @param value duration in milliseconds
 * @return value as string with decimal point in corresponding language
 */
export function formatDuration(
  value: number | null | undefined,
  minuteView = false,
  isSecond = false,
  addSpaceBefore = true,
): string {
  if (value == null) {
    return "";
  }
  if (minuteView) {
    if (value < 0) {
      return `-${Duration.fromMillis(value * -1 * (isSecond ? 1000 : 1)).toFormat("hh:mm")}`;
    }
    return `${addSpaceBefore ? "\xa0" : ""}${Duration.fromMillis(value * (isSecond ? 1000 : 1)).toFormat("hh:mm")}`;
  }

  const decimalSeparator =
    Intl.NumberFormat(locale.value)
      .formatToParts(1.1)
      .find((part) => part.type === "decimal")?.value ?? ",";
  const hourString = (Math.abs(value * (isSecond ? 1000 : 1)) / 3600000).toFixed(2);
  const result = `0${hourString}`
    .slice(-Math.max(hourString.length, 5))
    .replace(".", decimalSeparator);

  return value < 0 ? `-${result}` : `${addSpaceBefore ? "\xa0" : ""}${result}`; // <-- This is not a regular space, its a non-breaking space to keep space as same as minus (https://en.wikipedia.org/wiki/Non-breaking_space)
}

type DateRangeObject = {
  start: string;
  end: string;
  startInclusive: boolean;
  endInclusive: boolean;
};

export function getDateRangeFromStringToObject(value: string) {
  if (typeof value !== "string") {
    throw new TypeError(`PGDateRange cannot represent non-string value: ${value}`);
  }
  // match date range as string with regex with inclusive and exclusive
  const regex = /^[[(](\d{4}-\d{2}-\d{2}),(\d{4}-\d{2}-\d{2})[\])]$/;
  const match = value.match(regex);
  if (!match) {
    throw new TypeError(`PGDateRange cannot represent non-PGDateRange value: ${value}`);
  }
  const startInclusive = match[0][0] === "[";
  const start = match[1];
  const end = match[2];
  const endInclusive = match[0][match[0].length - 1] === "]";
  return {
    start,
    end,
    startInclusive,
    endInclusive,
  };
}

export function getDateRangeFromObjectToString(value: DateRangeObject) {
  if (typeof value !== "object" || value === null) {
    throw new TypeError(`PGDateRange cannot represent non-object value: ${value}`);
  }
  if (!("start" in value) || !("end" in value)) {
    throw new TypeError(`PGDateRange cannot represent non-PGDateRange value: ${value}`);
  }
  if (typeof value.start !== "string" || typeof value.end !== "string") {
    throw new TypeError(`PGDateRange cannot represent non-PGDateRange value: ${value}`);
  }
  const startInclusive = ("startInclusive" in value && value.startInclusive) ?? true;
  const endInclusive = ("endInclusive" in value && value.endInclusive) ?? false;
  const start = value.start;
  const end = value.end;
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(start) || !regex.test(end)) {
    throw new TypeError(`PGDateRange cannot represent non-PGDateRange value: ${value}`);
  }
  return `${startInclusive ? "[" : "("}${start},${end}${endInclusive ? "]" : ")"}`;
}

export function formatTime(element: string, tz: string) {
  return DateTime.fromISO(element).setZone(tz).toLocaleString({
    hour: "2-digit",
    minute: "2-digit",
  });
}
